import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'SUITES', path: '/suites' },
    { name: 'AMENITIES', path: '/amenities' },
    { name: 'GALLERY', path: '/gallery' },
    { name: 'ABOUT', path: '/about' },
    { name: 'CONTACT', path: '/contact' },
  ];

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'backdrop-blur-xl bg-[#FAF8F5]/80 border-b border-[#1A1A1A]/5' : 'bg-transparent'
        }`}
      >
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" data-testid="header-logo-link" className="flex items-center">
              <h1 className="font-serif font-bold tracking-tight text-[#1E3A8A]" style={{ fontSize: '28px' }} data-testid="header-logo-text">
                De Azraa
              </h1>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`text-sm tracking-widest uppercase font-medium transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-[#C9A84C]'
                      : 'text-[#1A1A1A] hover:text-[#C9A84C]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Book Now Button */}
            <Link
              to="/book"
              data-testid="header-book-now-btn"
              className="hidden lg:block bg-[#1A1A1A] text-[#FAF8F5] hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 px-8 py-3 tracking-widest uppercase"
              style={{ fontSize: '16px' }}
            >
              RESERVE NOW
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-[#1A1A1A]"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
            className="fixed top-[72px] left-0 right-0 bg-[#FAF8F5] border-b border-[#1A1A1A]/10 z-40 lg:hidden"
          >
            <nav className="flex flex-col px-6 py-6 gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-sm tracking-widest uppercase font-medium py-2 transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-[#C9A84C]'
                      : 'text-[#1A1A1A] hover:text-[#C9A84C]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/book"
                data-testid="mobile-book-now-btn"
                onClick={() => setIsMobileMenuOpen(false)}
                className="bg-[#1A1A1A] text-[#FAF8F5] hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 px-8 py-3 tracking-widest uppercase text-center mt-4"
                style={{ fontSize: '16px' }}
              >
                RESERVE NOW
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;